import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Search, LayoutGrid, List, Loader2, Pencil, Trash2, ExternalLink, BarChart3,
  Building2, Phone, Mail, Calendar as CalendarIcon, AlertTriangle, MessageSquare,
  Send, ArrowRight, X, Globe, Trophy, XCircle, History, UserCog, MessageCircle,
  Paperclip, Upload, Tag, MapPin, FileText, Image as ImageIcon, AtSign,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import { todayISO } from "@/lib/format";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { LeadDescripcion, LeadChecklist, LeadAddUrl, LeadPresupuestos } from "@/components/LeadExtras";

const eur = (n) => new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n || 0);

const ESTADO_META = {
  nuevo:               { label: "Nuevo",                color: "bg-zinc-500/15 text-zinc-200 border-zinc-500/30",     dot: "bg-zinc-400" },
  contactado:          { label: "Contactado",           color: "bg-blue-500/15 text-blue-200 border-blue-500/30",     dot: "bg-blue-400" },
  reunion_agendada:    { label: "Reunión agendada",     color: "bg-indigo-500/15 text-indigo-200 border-indigo-500/30", dot: "bg-indigo-400" },
  presupuesto_enviado: { label: "Presupuesto enviado",  color: "bg-amber-500/15 text-amber-200 border-amber-500/30",  dot: "bg-amber-400" },
  negociacion:         { label: "Negociación",          color: "bg-orange-500/15 text-orange-200 border-orange-500/30", dot: "bg-orange-400" },
  ganado_parcial:      { label: "Ganado parcial",       color: "bg-teal-500/15 text-teal-200 border-teal-500/30",   dot: "bg-teal-400" },
  ganado:              { label: "Ganado",               color: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30", dot: "bg-emerald-400" },
  perdido:             { label: "Perdido",              color: "bg-red-500/15 text-red-200 border-red-500/30",       dot: "bg-red-400" },
};

const KANBAN_ORDER = ["nuevo", "contactado", "reunion_agendada", "presupuesto_enviado", "negociacion", "ganado_parcial", "ganado"];

const EMPTY = {
  empresa: "", persona_contacto: "", email: "", telefono: "", cif: "", web: "",
  fuente: "Otros", estado: "nuevo", comercial_id: "",
  importe_estimado: "", probabilidad: 10,
  proxima_accion: "", proxima_accion_fecha: "",
  descripcion_oportunidad: "", notas: "",
  etiquetas: [], ubicacion_espacio: "", whatsapp_group_url: "", cliente_existente_id: "",
};

export default function Leads() {
  const { user, hasRole } = useAuth();
  const nav = useNavigate();
  const isAdmin = hasRole("admin");

  const [meta, setMeta] = useState({ estados: [], fuentes: [], probabilidad_por_estado: {} });
  const [leads, setLeads] = useState([]);
  const [resumen, setResumen] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [clientesDir, setClientesDir] = useState([]);

  const [view, setView] = useState("kanban"); // kanban | list
  const [search, setSearch] = useState("");
  const [filtroFuente, setFiltroFuente] = useState("");
  const [filtroComercial, setFiltroComercial] = useState("");
  const [loading, setLoading] = useState(true);

  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const [detailId, setDetailId] = useState(() => new URLSearchParams(window.location.search).get("lead"));
  const [dragging, setDragging] = useState(null);

  const loadMeta = async () => {
    try {
      const [m, u, cli] = await Promise.all([
        api.get("/leads/meta"),
        api.get("/users"),
        api.get("/clientes"),
      ]);
      setMeta(m.data);
      setUsuarios(u.data || []);
      setClientesDir(cli.data || []);
    } catch (err) {
      toast.error("Error cargando metadata");
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.q = search;
      if (filtroFuente) params.fuente = filtroFuente;
      if (filtroComercial) params.comercial_id = filtroComercial;
      const [l, r] = await Promise.all([
        api.get("/leads", { params }),
        api.get("/leads/resumen"),
      ]);
      setLeads(l.data || []);
      setResumen(r.data || null);
    } catch (err) {
      toast.error("Error cargando leads");
    } finally {
      setLoading(false);
    }
  }, [search, filtroFuente, filtroComercial]);

  useEffect(() => { loadMeta(); }, []);
  useEffect(() => { load(); }, [load]);

  // ---- Kanban data
  const kanbanColumns = useMemo(() => {
    const cols = {};
    KANBAN_ORDER.forEach((e) => { cols[e] = []; });
    leads.forEach((l) => {
      if (KANBAN_ORDER.includes(l.estado)) cols[l.estado].push(l);
    });
    return cols;
  }, [leads]);

  const perdidos = useMemo(() => leads.filter((l) => l.estado === "perdido"), [leads]);

  // ---- Handlers
  const submitNew = async (e) => {
    e?.preventDefault();
    if (!form.empresa.trim()) return toast.error("La empresa es obligatoria");
    setSaving(true);
    try {
      const payload = {
        ...form,
        importe_estimado: parseFloat(form.importe_estimado || 0),
        probabilidad: parseInt(form.probabilidad, 10) || 0,
        proxima_accion: form.proxima_accion || null,
        proxima_accion_fecha: form.proxima_accion_fecha || null,
        comercial_id: form.comercial_id || null,
      };
      const { data } = await api.post("/leads", payload);
      toast.success(`Lead «${data.empresa}» creado`);
      setOpenNew(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const moveTo = async (lead, nuevoEstado) => {
    if (lead.estado === nuevoEstado) return;
    // Optimista
    setLeads((curr) => curr.map((l) => l.id === lead.id ? { ...l, estado: nuevoEstado } : l));
    try {
      await api.patch(`/leads/${lead.id}`, { estado: nuevoEstado });
      toast.success(`«${lead.empresa}» → ${ESTADO_META[nuevoEstado]?.label}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error moviendo lead");
      load();
    }
  };

  // ---- HTML5 drag&drop
  const onDragStart = (lead) => (e) => {
    setDragging(lead);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; };
  const onDrop = (estado) => (e) => {
    e.preventDefault();
    if (!dragging) return;
    moveTo(dragging, estado);
    setDragging(null);
  };

  const remove = async (lead) => {
    if (!isAdmin) return toast.error("Sólo admin");
    if (!window.confirm(`¿Eliminar lead «${lead.empresa}»?`)) return;
    try {
      await api.delete(`/leads/${lead.id}`);
      toast.success("Lead eliminado");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar");
    }
  };

  return (
    <div className="p-6" data-testid="leads-page">
      <PageHeader
        title="CRM · Leads"
        subtitle="Oportunidades comerciales en pipeline"
        action={
          <div className="flex items-center gap-2">
            <div className="flex bg-zinc-900/60 border border-zinc-800 rounded-md p-0.5">
              <button onClick={() => setView("kanban")} data-testid="view-kanban"
                className={`px-2.5 py-1 rounded text-xs flex items-center gap-1 ${view === "kanban" ? "bg-amber-500/20 text-amber-200" : "text-zinc-400 hover:text-zinc-100"}`}>
                <LayoutGrid className="w-3.5 h-3.5" /> Kanban
              </button>
              <button onClick={() => setView("list")} data-testid="view-list"
                className={`px-2.5 py-1 rounded text-xs flex items-center gap-1 ${view === "list" ? "bg-amber-500/20 text-amber-200" : "text-zinc-400 hover:text-zinc-100"}`}>
                <List className="w-3.5 h-3.5" /> Lista
              </button>
            </div>
            {hasRole("admin") && (
              <Button onClick={() => nav("/leads-analytics")} variant="outline"
                className="bg-transparent border-zinc-700 text-zinc-300 hover:text-amber-300 hover:border-amber-500/40"
                data-testid="leads-analytics-btn">
                <BarChart3 className="w-4 h-4 mr-1.5" /> Analítica
              </Button>
            )}
            <Button onClick={() => { setForm(EMPTY); setOpenNew(true); }}
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="leads-new-btn">
              <Plus className="w-4 h-4 mr-2" /> Nuevo lead
            </Button>
          </div>
        }
      />

      {/* KPIs */}
      {resumen && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
          <KPI label="Pipeline activo" value={eur(resumen.valor_pipeline)} hi testid="kpi-pipeline" />
          <KPI label="Valor ponderado" value={eur(resumen.valor_ponderado)} accent="text-sky-300" testid="kpi-ponderado" />
          <KPI label="Ganado este mes" value={eur(resumen.valor_ganado_mes)} accent="text-emerald-300" testid="kpi-ganado" />
          <KPI label="Tasa conversión" value={`${resumen.tasa_conversion}%`} accent="text-amber-300" testid="kpi-conv" />
          <KPI label="Acciones pendientes"
            value={resumen.vencidos + resumen.hoy}
            accent={(resumen.vencidos > 0) ? "text-red-300" : "text-zinc-200"}
            testid="kpi-pendientes" />
        </div>
      )}

      {/* Filtros */}
      <div className="bg-zinc-900/40 border border-zinc-800/70 rounded-lg p-3 mb-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-3.5 h-3.5 absolute left-2.5 top-2.5 text-zinc-500" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar empresa, contacto, email…"
            className="bg-zinc-950 border-zinc-800 pl-8" data-testid="filter-search" />
        </div>
        <Select value={filtroFuente || "__ALL__"} onValueChange={(v) => setFiltroFuente(v === "__ALL__" ? "" : v)}>
          <SelectTrigger className="bg-zinc-950 border-zinc-800 w-44" data-testid="filter-fuente">
            <SelectValue placeholder="Fuente" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
            <SelectItem value="__ALL__">Todas las fuentes</SelectItem>
            {meta.fuentes.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filtroComercial || "__ALL__"} onValueChange={(v) => setFiltroComercial(v === "__ALL__" ? "" : v)}>
          <SelectTrigger className="bg-zinc-950 border-zinc-800 w-48" data-testid="filter-comercial">
            <SelectValue placeholder="Comercial" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
            <SelectItem value="__ALL__">Todos los comerciales</SelectItem>
            {usuarios.filter((u) => (u.roles || []).some((r) => r === "comercial" || r === "admin")).map((u) => (
              <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {(search || filtroFuente || filtroComercial) && (
          <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setFiltroFuente(""); setFiltroComercial(""); }}
            className="text-zinc-400 hover:text-zinc-100" data-testid="filter-clear">
            <X className="w-3.5 h-3.5 mr-1" /> Limpiar
          </Button>
        )}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-500">
          <Loader2 className="w-6 h-6 animate-spin mr-2" /> Cargando…
        </div>
      ) : view === "kanban" ? (
        <div className="overflow-x-auto" data-testid="kanban-board">
          <div className="grid grid-flow-col auto-cols-[280px] gap-3 pb-4">
            {KANBAN_ORDER.map((estado) => {
              const m = ESTADO_META[estado];
              const items = kanbanColumns[estado] || [];
              const totalCol = items.reduce((s, l) => s + (l.importe_estimado || 0), 0);
              return (
                <div key={estado}
                  className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg flex flex-col min-h-[400px]"
                  onDragOver={onDragOver}
                  onDrop={onDrop(estado)}
                  data-testid={`kanban-col-${estado}`}
                >
                  <div className={`px-3 py-2 rounded-t-lg flex items-center justify-between border-b border-zinc-800 ${m.color}`}>
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${m.dot}`}></span>
                      <span className="text-xs font-semibold uppercase tracking-wider">{m.label}</span>
                    </div>
                    <div className="text-[10px] text-zinc-400">
                      {items.length} · {eur(totalCol)}
                    </div>
                  </div>
                  <div className="p-2 space-y-2 flex-1">
                    {items.map((lead) => (
                      <KanbanCard key={lead.id} lead={lead}
                        onClick={() => setDetailId(lead.id)}
                        onDragStart={onDragStart(lead)}
                      />
                    ))}
                    {items.length === 0 && (
                      <div className="text-center text-xs text-zinc-600 py-8 italic">
                        — vacía —
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {perdidos.length > 0 && (
            <details className="bg-zinc-900/20 border border-zinc-800/60 rounded-lg p-3 mt-3" data-testid="perdidos-section">
              <summary className="cursor-pointer text-xs text-zinc-400 hover:text-zinc-100 flex items-center gap-2">
                <XCircle className="w-3.5 h-3.5 text-red-400" />
                Perdidos ({perdidos.length}) — {eur(perdidos.reduce((s, l) => s + (l.importe_estimado || 0), 0))}
              </summary>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
                {perdidos.map((lead) => (
                  <KanbanCard key={lead.id} lead={lead} onClick={() => setDetailId(lead.id)} compact />
                ))}
              </div>
            </details>
          )}
        </div>
      ) : (
        // ----- LISTA -----
        <div className="bg-zinc-900/30 border border-zinc-800/60 rounded-lg overflow-hidden" data-testid="leads-list">
          {leads.length === 0 ? (
            <div className="text-center text-zinc-500 py-12">No hay leads aún.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-800">
                <tr>
                  <th className="text-left p-2.5">Empresa</th>
                  <th className="text-left p-2.5">Estado</th>
                  <th className="text-left p-2.5">Fuente</th>
                  <th className="text-left p-2.5">Comercial</th>
                  <th className="text-right p-2.5">Importe</th>
                  <th className="text-center p-2.5">Prob</th>
                  <th className="text-left p-2.5">Próxima acción</th>
                  <th className="p-2.5"></th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => {
                  const m = ESTADO_META[lead.estado] || ESTADO_META.nuevo;
                  return (
                    <tr key={lead.id}
                      onClick={() => setDetailId(lead.id)}
                      className="border-b border-zinc-800/60 hover:bg-zinc-900/40 cursor-pointer"
                      data-testid={`lead-row-${lead.id}`}>
                      <td className="p-2.5">
                        <div className="font-medium text-zinc-100">{lead.empresa}</div>
                        <div className="text-xs text-zinc-500">{lead.persona_contacto || "—"}</div>
                      </td>
                      <td className="p-2.5">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full border ${m.color}`}>{m.label}</span>
                      </td>
                      <td className="p-2.5 text-xs text-zinc-300">{lead.fuente}</td>
                      <td className="p-2.5 text-xs text-zinc-300">{lead.comercial_nombre || "—"}</td>
                      <td className="p-2.5 text-right text-zinc-100">{eur(lead.importe_estimado)}</td>
                      <td className="p-2.5 text-center text-zinc-300 text-xs">{lead.probabilidad}%</td>
                      <td className="p-2.5 text-xs">
                        {lead.proxima_accion_fecha ? (
                          <span className={lead.accion_vencida ? "text-red-300 font-medium" : lead.accion_hoy ? "text-amber-300 font-medium" : "text-zinc-400"}>
                            {lead.accion_vencida && <AlertTriangle className="w-3 h-3 inline mr-1" />}
                            {lead.proxima_accion_fecha} · {lead.proxima_accion || "—"}
                          </span>
                        ) : <span className="text-zinc-600">—</span>}
                      </td>
                      <td className="p-2.5 text-right" onClick={(e) => e.stopPropagation()}>
                        {isAdmin && (
                          <Button size="icon" variant="ghost" onClick={() => remove(lead)}
                            className="h-7 w-7 text-zinc-400 hover:text-red-400"
                            data-testid={`lead-delete-${lead.id}`}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Dialog NUEVO lead */}
      <Dialog open={openNew} onOpenChange={setOpenNew}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-xl max-h-[90vh] overflow-y-auto"
          data-testid="lead-new-dialog">
          <DialogHeader>
            <DialogTitle>Nuevo lead</DialogTitle>
            <DialogDescription className="text-zinc-500 text-xs">
              Captura una nueva oportunidad comercial.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitNew} className="space-y-3">
            <div className="bg-amber-500/5 border border-amber-500/20 rounded p-2.5">
              <Label className="text-xs text-zinc-400 flex items-center gap-1 mb-1">
                <Building2 className="w-3 h-3 text-amber-400" /> ¿Es un cliente del directorio?
                <span className="text-[10px] text-zinc-600 normal-case">(opcional · autocompleta los datos)</span>
              </Label>
              <Select
                value={form.cliente_existente_id || "__NONE__"}
                onValueChange={(v) => {
                  if (v === "__NONE__") {
                    setForm({ ...form, cliente_existente_id: "" });
                    return;
                  }
                  const cli = clientesDir.find((c) => c.id === v);
                  if (cli) {
                    setForm({
                      ...form,
                      cliente_existente_id: v,
                      empresa: cli.nombre || form.empresa,
                      cif: cli.cif || form.cif,
                      telefono: cli.telefono || form.telefono,
                      email: cli.email_contacto || form.email,
                      web: cli.web || form.web,
                    });
                  } else {
                    setForm({ ...form, cliente_existente_id: v });
                  }
                }}
              >
                <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="form-cliente-existente">
                  <SelectValue placeholder="— Crear lead nuevo (sin vincular) —" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-64">
                  <SelectItem value="__NONE__">— No, es un cliente nuevo —</SelectItem>
                  {clientesDir
                    .slice()
                    .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Empresa *</Label>
              <Input value={form.empresa} onChange={(e) => setForm({ ...form, empresa: e.target.value })}
                className="bg-zinc-900 border-zinc-800" data-testid="form-empresa" required autoFocus />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Persona contacto</Label>
                <Input value={form.persona_contacto} onChange={(e) => setForm({ ...form, persona_contacto: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="form-persona" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="form-email" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Teléfono</Label>
                <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="form-telefono" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">CIF</Label>
                <Input value={form.cif} onChange={(e) => setForm({ ...form, cif: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="form-cif" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-zinc-400">Web</Label>
                <Input value={form.web} onChange={(e) => setForm({ ...form, web: e.target.value })}
                  placeholder="https://…"
                  className="bg-zinc-900 border-zinc-800" data-testid="form-web" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Fuente</Label>
                <Select value={form.fuente} onValueChange={(v) => setForm({ ...form, fuente: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="form-fuente"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {meta.fuentes.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Comercial asignado</Label>
                <Select value={form.comercial_id || "__NONE__"}
                  onValueChange={(v) => setForm({ ...form, comercial_id: v === "__NONE__" ? "" : v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="form-comercial"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    <SelectItem value="__NONE__">— sin asignar —</SelectItem>
                    {usuarios.filter((u) => (u.roles || []).some((r) => r === "comercial" || r === "admin")).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Importe estimado (€)</Label>
                <Input type="number" step="0.01" min="0" value={form.importe_estimado}
                  onChange={(e) => setForm({ ...form, importe_estimado: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="form-importe" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Probabilidad (%)</Label>
                <Input type="number" min="0" max="100" value={form.probabilidad}
                  onChange={(e) => setForm({ ...form, probabilidad: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="form-prob" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400">Descripción de la oportunidad</Label>
              <Textarea value={form.descripcion_oportunidad}
                onChange={(e) => setForm({ ...form, descripcion_oportunidad: e.target.value })}
                placeholder="¿Qué necesita el cliente?"
                className="bg-zinc-900 border-zinc-800 min-h-[60px]" data-testid="form-desc" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400">Próxima acción</Label>
                <Input value={form.proxima_accion}
                  onChange={(e) => setForm({ ...form, proxima_accion: e.target.value })}
                  placeholder="Ej. Llamar y enviar muestra"
                  className="bg-zinc-900 border-zinc-800" data-testid="form-accion" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Fecha próxima acción</Label>
                <Input type="date" value={form.proxima_accion_fecha}
                  onChange={(e) => setForm({ ...form, proxima_accion_fecha: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" data-testid="form-accion-fecha" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-zinc-400 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Ubicación del trabajo
                </Label>
                <AddressAutocomplete
                  value={form.ubicacion_espacio}
                  onChange={(v) => setForm({ ...form, ubicacion_espacio: v })}
                  onSelect={(loc) => setForm({ ...form, ubicacion_espacio: loc.direccion })}
                  placeholder="Empieza a escribir: CC Holea, Mango Pg Gracia…"
                  data-testid="form-ubicacion"
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400 flex items-center gap-1">
                  <MessageCircle className="w-3 h-3 text-emerald-400" /> URL grupo WhatsApp
                </Label>
                <Input value={form.whatsapp_group_url}
                  onChange={(e) => setForm({ ...form, whatsapp_group_url: e.target.value })}
                  placeholder="https://chat.whatsapp.com/…"
                  data-testid="form-wa-group"
                  className="bg-zinc-900 border-zinc-800" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-zinc-400 flex items-center gap-1">
                <Tag className="w-3 h-3" /> Etiquetas (#hashtags)
              </Label>
              <TagEditor
                value={form.etiquetas || []}
                onChange={(tags) => setForm({ ...form, etiquetas: tags })}
                sugerencias={["montaje", "produccion", "diseno", "rotulacion", "vinilo", "escaparate"]}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpenNew(false)}
                className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
              <Button type="submit" disabled={saving}
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="form-save">
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando…</> : "Crear lead"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Detail drawer */}
      {detailId && (
        <LeadDetail
          leadId={detailId}
          onClose={() => setDetailId(null)}
          onChange={load}
          usuarios={usuarios}
          meta={meta}
          clientesDir={clientesDir}
        />
      )}
    </div>
  );
}

// ============== KANBAN CARD ==============
function LeadEconomia({ lead }) {
  const rc = lead.resumen_comercial;
  if (!rc || !rc.n_partidas) return null;
  const est = lead.estado;
  const pct = rc.presupuestado > 0 ? Math.round((rc.convertido / rc.presupuestado) * 100) : 0;
  const nPet = rc.n_peticiones ?? (rc.peticiones_ids || []).length;
  const petTxt = nPet > 0 ? ` · ${nPet} ${nPet === 1 ? "petición" : "peticiones"}` : "";
  const tip = `Base imponible sin IVA · Presupuestado ${eur(rc.presupuestado)} · Ganado ${eur(rc.convertido)} · Pendiente ${eur(rc.pendiente)}` +
    (rc.pospuesto > 0 ? ` (pospuesto ${eur(rc.pospuesto)})` : "") + (rc.rechazado > 0 ? ` · Rechazado ${eur(rc.rechazado)}` : "");
  const Amt = ({ label, value, cls, testid }) => (
    <span className="flex items-baseline gap-1 min-w-0" data-testid={testid}>
      <span className="text-[9px] uppercase tracking-wider text-zinc-500">{label}</span>
      <span className={`text-xs font-semibold tabular-nums ${cls}`}>{eur(value)}</span>
    </span>
  );
  const parcial = est === "ganado_parcial";
  const ganado = est === "ganado";
  const enviado = est === "presupuesto_enviado" || est === "negociacion";
  return (
    <div title={tip} data-testid={`lead-card-eco-${lead.id}`}
      className={`mt-1.5 rounded-md border px-2 py-1.5 ${parcial ? "border-emerald-500/30 bg-emerald-500/5" : ganado ? "border-emerald-500/20 bg-zinc-900/60" : "border-zinc-800 bg-zinc-900/60"}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        {parcial && <><Amt label="Ganado" value={rc.convertido} cls="text-emerald-300" testid="eco-ganado" /><Amt label="Pend." value={rc.pendiente} cls="text-amber-200" testid="eco-pendiente" /></>}
        {ganado && <><Amt label="Ganado" value={rc.convertido} cls="text-emerald-300" testid="eco-ganado" />{rc.rechazado > 0 && <Amt label="Rech." value={rc.rechazado} cls="text-zinc-500" testid="eco-rechazado" />}</>}
        {enviado && <><Amt label="Presup." value={rc.presupuestado} cls="text-zinc-100" testid="eco-presupuestado" />
          {rc.convertido > 0 ? <Amt label="Ganado" value={rc.convertido} cls="text-emerald-300" testid="eco-ganado" /> : rc.pendiente > 0 && <Amt label="Pend." value={rc.pendiente} cls="text-amber-200" testid="eco-pendiente" />}</>}
        {!parcial && !ganado && !enviado && <Amt label="Presup." value={rc.presupuestado} cls="text-zinc-100" testid="eco-presupuestado" />}
      </div>
      {(parcial || ganado) && (
        <div className="h-1 mt-1.5 rounded-full bg-zinc-800 overflow-hidden" data-testid="eco-progress">
          <div className="h-full bg-emerald-500/80 rounded-full" style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
      )}
      <div className="flex items-center justify-between mt-1 text-[10px] text-zinc-500">
        <span data-testid="eco-partidas">{rc.n_convertidas}/{rc.n_partidas} partidas{petTxt}</span>
        <span className="text-zinc-600">sin IVA</span>
      </div>
    </div>
  );
}

function KanbanCard({ lead, onClick, onDragStart, compact }) {
  const m = ESTADO_META[lead.estado];
  return (
    <div
      draggable={!!onDragStart}
      onDragStart={onDragStart}
      onClick={onClick}
      data-testid={`lead-card-${lead.id}`}
      className={`bg-zinc-950 border border-zinc-800 hover:border-amber-500/50 rounded p-2.5 cursor-pointer transition group ${compact ? "" : ""}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="font-semibold text-sm text-zinc-100 truncate">{lead.empresa}</div>
        {lead.accion_vencida && <AlertTriangle className="w-3.5 h-3.5 text-red-400 shrink-0" title="Acción vencida" />}
        {lead.accion_hoy && !lead.accion_vencida && <CalendarIcon className="w-3.5 h-3.5 text-amber-400 shrink-0" title="Acción hoy" />}
      </div>
      {lead.persona_contacto && (
        <div className="text-[11px] text-zinc-400 truncate">{lead.persona_contacto}</div>
      )}
      <div className="flex items-center justify-between mt-1.5 text-[11px]">
        <span className="text-zinc-300 font-medium">{eur(lead.importe_estimado)}</span>
        <span className="text-zinc-500">{lead.probabilidad}%</span>
      </div>
      {!compact && <LeadEconomia lead={lead} />}
      {lead.proxima_accion_fecha && !compact && (
        <div className={`text-[10px] mt-1 truncate ${lead.accion_vencida ? "text-red-400" : lead.accion_hoy ? "text-amber-400" : "text-zinc-500"}`}>
          <CalendarIcon className="w-3 h-3 inline mr-0.5" />
          {lead.proxima_accion_fecha} · {lead.proxima_accion || "—"}
        </div>
      )}
      {lead.comercial_nombre && !compact && (
        <div className="text-[10px] mt-1 text-zinc-500 flex items-center gap-1">
          <UserCog className="w-3 h-3" /> {lead.comercial_nombre}
        </div>
      )}
    </div>
  );
}

// ============== KPI ==============
function KPI({ label, value, accent = "text-zinc-100", hi = false, testid }) {
  return (
    <div className={`rounded-lg border ${hi ? "border-amber-500/40 bg-amber-500/5" : "border-zinc-800 bg-zinc-900/40"} px-4 py-3`}
      data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className={`text-lg font-bold ${hi ? "text-amber-300" : accent}`}>{value}</div>
    </div>
  );
}

// ============== LEAD DETAIL ==============
function LeadDetail({ leadId, onClose, onChange, usuarios, meta, clientesDir = [] }) {
  const [lead, setLead] = useState(null);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});
  const [comentario, setComentario] = useState("");
  const [openConvert, setOpenConvert] = useState(false);
  const [openPerdido, setOpenPerdido] = useState(false);
  const [motivoPerdida, setMotivoPerdida] = useState("");
  const [convForm, setConvForm] = useState({ crear_peticion: true, peticion_nombre: "", peticion_descripcion: "" });
  // CRM Phase 1: usuarios mencionables, autocomplete @, upload archivos
  const [mencionables, setMencionables] = useState([]);
  const [mentionPopup, setMentionPopup] = useState(null); // {match, options, anchor}
  const [uploadingFile, setUploadingFile] = useState(false);
  const [draggingFile, setDraggingFile] = useState(false);
  const fileInputRef = React.useRef(null);
  const comentarioRef = React.useRef(null);
  const idemRef = React.useRef(null);

  // Cargar usuarios mencionables una sola vez (clientes vienen como prop)
  useEffect(() => {
    api.get("/leads/usuarios-mencionables").then((r) => setMencionables(r.data || [])).catch(() => {});
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/leads/${leadId}`);
      setLead(data);
      setEdit({
        empresa: data.empresa || "",
        persona_contacto: data.persona_contacto || "",
        email: data.email || "",
        telefono: data.telefono || "",
        cif: data.cif || "",
        web: data.web || "",
        fuente: data.fuente || "Otros",
        comercial_id: data.comercial_id || "",
        importe_estimado: data.importe_estimado ?? 0,
        probabilidad: data.probabilidad ?? 0,
        proxima_accion: data.proxima_accion || "",
        proxima_accion_fecha: data.proxima_accion_fecha || "",
        descripcion_oportunidad: data.descripcion_oportunidad || "",
        notas: data.notas || "",
        etiquetas: data.etiquetas || [],
        ubicacion_espacio: data.ubicacion_espacio || "",
        whatsapp_group_url: data.whatsapp_group_url || "",
        cliente_existente_id: data.cliente_existente_id || "",
      });
    } catch (err) {
      toast.error("Error cargando lead");
      onClose();
    }
  }, [leadId, onClose]);
  useEffect(() => { load(); }, [load]);

  if (!lead) return null;
  const m = ESTADO_META[lead.estado];
  const yaConvertido = !!lead.cliente_id;

  const saveEdit = async () => {
    try {
      const payload = {
        ...edit,
        importe_estimado: parseFloat(edit.importe_estimado || 0),
        probabilidad: parseInt(edit.probabilidad, 10) || 0,
        comercial_id: edit.comercial_id || null,
        proxima_accion: edit.proxima_accion || null,
        proxima_accion_fecha: edit.proxima_accion_fecha || null,
        // Phase 1 CRM:
        etiquetas: edit.etiquetas || [],
        ubicacion_espacio: edit.ubicacion_espacio || null,
        whatsapp_group_url: edit.whatsapp_group_url || null,
        cliente_existente_id: edit.cliente_existente_id || null,
      };
      await api.patch(`/leads/${leadId}`, payload);
      toast.success("Lead actualizado");
      setEditing(false);
      load();
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  // Resuelve @handles del texto contra `mencionables` y devuelve user_ids
  const resolverMenciones = (texto) => {
    const handles = (texto.match(/@([A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9_.]+)/g) || [])
      .map((h) => h.slice(1).toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
    const ids = [];
    handles.forEach((h) => {
      const u = mencionables.find((m) => m.handle === h);
      if (u && !ids.includes(u.id)) ids.push(u.id);
    });
    return ids;
  };

  const addComment = async () => {
    if (!comentario.trim()) return;
    if (!idemRef.current) idemRef.current = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);
    try {
      const menciones_ids = resolverMenciones(comentario);
      const { data } = await api.post(`/leads/${leadId}/comentario`, { descripcion: comentario, menciones_ids, idempotency_key: idemRef.current });
      idemRef.current = null;
      setComentario("");
      setMentionPopup(null);
      const m = data?.menciones || [];
      const emails = m.filter((x) => x.estado_email === "enviado").length;
      const sinEmail = m.filter((x) => x.estado_email === "sin_email").length;
      const bloq = m.filter((x) => x.estado_email === "bloqueado_outbound_guard").length;
      if (menciones_ids.length > 0) {
        toast.success(`Comentario enviado · ${menciones_ids.length} mención(es) · ${emails} email(s)${sinEmail ? ` · ${sinEmail} sin email` : ""}${bloq ? ` · ${bloq} bloqueado(s) en preview` : ""}`);
      }
      load();
    } catch (err) {
      toast.error("Error añadiendo comentario (puedes reintentar: no se duplicará)");
    }
  };

  // Detectar @ mientras se escribe → mostrar popup de autocomplete
  const onComentarioChange = (e) => {
    const val = e.target.value;
    setComentario(val);
    const caret = e.target.selectionStart;
    const before = val.slice(0, caret);
    const m = before.match(/@([A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9_.]*)$/);
    if (m) {
      const q = m[1].toLowerCase()
        .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const opts = mencionables.filter((u) =>
        u.handle.startsWith(q) || (u.name || "").toLowerCase().includes(q)
      ).slice(0, 6);
      setMentionPopup({ q, opts, caret });
    } else {
      setMentionPopup(null);
    }
  };

  const pickMention = (u) => {
    if (!mentionPopup) return;
    const caret = mentionPopup.caret;
    const before = comentario.slice(0, caret);
    const after = comentario.slice(caret);
    const newBefore = before.replace(/@[A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9_.]*$/, `@${u.handle} `);
    const newVal = newBefore + after;
    setComentario(newVal);
    setMentionPopup(null);
    setTimeout(() => comentarioRef.current?.focus(), 0);
  };

  // Subir archivo a Cloudinary y registrarlo en el lead
  const uploadFile = async (file) => {
    if (!file) return;
    if (file.size > 20_000_000) {
      toast.error("Archivo demasiado grande (máx 20MB)");
      return;
    }
    setUploadingFile(true);
    try {
      const { uploadToCloudinary } = await import("@/lib/cloudinary");
      const ext = (file.name.split(".").pop() || "").toLowerCase();
      const resourceType = ["jpg", "jpeg", "png", "gif", "webp"].includes(ext) ? "image" : "auto";
      const up = await uploadToCloudinary(file, "dnf/leads/archivos", resourceType);
      await api.post(`/leads/${leadId}/archivos`, {
        nombre: file.name,
        url: up.secure_url,
        public_id: up.public_id,
        tipo: file.type,
        size_bytes: file.size,
      });
      toast.success(`Archivo "${file.name}" subido`);
      load();
    } catch (err) {
      toast.error("Error subiendo archivo: " + (err.message || err));
    } finally {
      setUploadingFile(false);
    }
  };

  const deleteFile = async (aid, nombre) => {
    if (!window.confirm(`¿Borrar "${nombre}"?`)) return;
    try {
      await api.delete(`/leads/${leadId}/archivos/${aid}`);
      toast.success("Archivo eliminado");
      load();
    } catch (err) {
      toast.error("Error borrando archivo");
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDraggingFile(false);
    const files = Array.from(e.dataTransfer.files || []);
    files.forEach(uploadFile);
  };

  const changeEstado = async (nuevo) => {
    if (nuevo === "perdido") {
      setOpenPerdido(true);
      return;
    }
    if (nuevo === "ganado" && !yaConvertido) {
      setConvForm((f) => ({ ...f, peticion_nombre: `Oportunidad · ${lead.empresa}` }));
      setOpenConvert(true);
      return;
    }
    try {
      await api.patch(`/leads/${leadId}`, { estado: nuevo });
      load();
      onChange();
    } catch (err) {
      toast.error("Error cambiando estado");
    }
  };

  const confirmPerdido = async () => {
    if (!motivoPerdida.trim()) return toast.error("Indica el motivo de pérdida");
    try {
      await api.patch(`/leads/${leadId}`, { estado: "perdido", motivo_perdida: motivoPerdida });
      toast.success("Lead marcado como perdido");
      setOpenPerdido(false);
      setMotivoPerdida("");
      load();
      onChange();
    } catch (err) {
      toast.error("Error");
    }
  };

  const convertir = async () => {
    try {
      const { data } = await api.post(`/leads/${leadId}/convertir`, convForm);
      toast.success(`Cliente «${data.cliente_nombre}» creado` + (data.peticion_id ? " + petición" : ""));
      setOpenConvert(false);
      load();
      onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al convertir");
    }
  };

  const sendWhatsApp = async () => {
    try {
      await api.post("/whatsapp/lead/recordatorio", { lead_id: leadId });
      toast.success("📱 Recordatorio enviado por WhatsApp");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error enviando WhatsApp");
    }
  };

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" overlayClassName="bg-black/35 backdrop-blur-[1px]"
        className="bg-zinc-950 border-l border-zinc-800 text-zinc-100 w-full sm:max-w-[min(1180px,94vw)] overflow-y-auto p-0"
        data-testid="lead-detail-dialog">
        {/* Cabecera compacta tipo tarea: título · estado · datos clave · acciones */}
        <SheetHeader className="sticky top-0 z-10 bg-zinc-950/95 backdrop-blur border-b border-zinc-800 px-6 py-4 space-y-2 text-left">
          <div className="flex items-start justify-between gap-3 pr-8">
            <div className="min-w-0">
              <div className="flex items-center gap-2 min-w-0">
                <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${m.dot || "bg-amber-400"}`} />
                <SheetTitle className="text-2xl font-semibold truncate leading-tight" data-testid="lead-drawer-title">{lead.empresa}</SheetTitle>
              </div>
              <SheetDescription className="text-zinc-500 text-xs mt-0.5">
                Oportunidad · {lead.fuente} · creada {(lead.created_at || "").slice(0, 10)}
                {lead.comercial_nombre && ` · ${lead.comercial_nombre}`}
              </SheetDescription>
            </div>
            <span className={`text-xs px-3 py-1 rounded-full border shrink-0 ${m.color}`} data-testid="lead-drawer-estado">{m.label}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-xs text-zinc-400" data-testid="lead-drawer-facts">
            <span><span className="text-zinc-600">Importe</span> <b className="text-zinc-100">{eur(lead.importe_estimado)}</b></span>
            <span><span className="text-zinc-600">Prob.</span> <b className="text-zinc-100">{lead.probabilidad}%</b></span>
            <span><span className="text-zinc-600">Próxima acción</span> <b className={lead.proxima_accion ? "text-amber-200" : "text-zinc-500"}>{lead.proxima_accion || "—"}</b>{lead.proxima_accion_fecha && <span className="text-zinc-500"> · {lead.proxima_accion_fecha}</span>}</span>
            {lead.persona_contacto && <span><span className="text-zinc-600">Contacto</span> <b className="text-zinc-100">{lead.persona_contacto}</b></span>}
            {(lead.presupuestos || []).length > 0 && <span className="text-emerald-300">{lead.presupuestos.length} presupuesto{lead.presupuestos.length > 1 ? "s" : ""}</span>}
          </div>
        </SheetHeader>
        <div className="px-6 py-4 space-y-4">

        {/* Acciones rápidas de estado */}
        {!yaConvertido && (
          <div className="flex flex-wrap gap-1.5 -mt-2 items-center">
            {KANBAN_ORDER.filter((e) => e !== lead.estado).map((e) => (
              <button key={e}
                onClick={() => changeEstado(e)}
                data-testid={`btn-estado-${e}`}
                className={`text-[10px] px-2 py-1 rounded border ${ESTADO_META[e].color} hover:brightness-125 transition`}>
                → {ESTADO_META[e].label}
              </button>
            ))}
            {lead.estado !== "perdido" && (
              <button onClick={() => changeEstado("perdido")}
                data-testid="btn-estado-perdido"
                className={`text-[10px] px-2 py-1 rounded border ${ESTADO_META.perdido.color} hover:brightness-125 transition`}>
                → Perdido
              </button>
            )}
            <div className="ml-auto">
              <Button size="sm" onClick={sendWhatsApp}
                disabled={!lead.proxima_accion_fecha}
                className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400 h-7 text-xs"
                title={lead.proxima_accion_fecha ? "Enviar recordatorio al comercial asignado" : "Define primero una próxima acción + fecha"}
                data-testid="btn-wa-recordatorio">
                <MessageCircle className="w-3.5 h-3.5 mr-1" /> Recordar WA
              </Button>
            </div>
          </div>
        )}

        {yaConvertido && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded p-3 text-sm">
            <Trophy className="w-4 h-4 text-emerald-300 inline mr-2" />
            Convertido a Cliente <b>{lead.cliente_nombre || ""}</b> el {(lead.convertido_at || "").slice(0, 10)}.
          </div>
        )}

        {/* Dos columnas: contenido + actividad (izq) · propiedades + presupuestos (der) */}
        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_340px] items-start">
        <aside className="space-y-4 order-2 lg:sticky lg:top-[112px]" data-testid="lead-drawer-props">
        {/* Datos principales / editor */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="text-xs uppercase tracking-wider text-zinc-500">Propiedades</h3>
            {!editing && !yaConvertido && (
              <Button size="sm" variant="ghost" onClick={() => setEditing(true)}
                className="text-zinc-400 hover:text-amber-300" data-testid="lead-edit-btn">
                <Pencil className="w-3 h-3 mr-1" /> Editar
              </Button>
            )}
          </div>

          {!editing ? (
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-3 text-sm">
              <Field label="Persona contacto" value={lead.persona_contacto} icon={UserCog} />
              <Field label="Email" value={lead.email} icon={Mail} link={lead.email ? `mailto:${lead.email}` : null} />
              <Field label="Teléfono" value={lead.telefono} icon={Phone} link={lead.telefono ? `tel:${lead.telefono}` : null} />
              <Field label="CIF" value={lead.cif} />
              <Field label="Web" value={lead.web} icon={Globe} link={lead.web} external />
              <Field label="Importe estimado" value={eur(lead.importe_estimado)} />
              <Field label="Probabilidad" value={`${lead.probabilidad}%`} />
              <Field label="Comercial" value={lead.comercial_nombre} />
              <Field label="Ubicación del trabajo" value={lead.ubicacion_espacio} icon={MapPin} />
              {lead.cliente_existente_id && (
                <Field
                  label="Vinculado a cliente del directorio"
                  value={(clientesDir.find((c) => c.id === lead.cliente_existente_id) || {}).nombre}
                  icon={Building2}
                />
              )}
              {lead.whatsapp_group_url && (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Grupo WhatsApp del trabajo</div>
                  <a href={lead.whatsapp_group_url} target="_blank" rel="noreferrer"
                    data-testid="lead-wa-group-link"
                    className="inline-flex items-center gap-1 text-emerald-300 hover:underline text-sm">
                    <MessageCircle className="w-3 h-3" /> {lead.whatsapp_group_url}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </div>
              )}
              {(lead.etiquetas || []).length > 0 && (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Etiquetas</div>
                  <div className="flex flex-wrap gap-1.5">
                    {lead.etiquetas.map((e) => (
                      <span key={e} data-testid={`lead-tag-${e}`}
                        className="text-[11px] px-2 py-0.5 rounded-full bg-violet-500/15 text-violet-200 border border-violet-500/30">
                        #{e}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Descripción oportunidad</div>
                <div className="text-sm text-zinc-200 whitespace-pre-wrap">{lead.descripcion_oportunidad || <span className="italic text-zinc-600">—</span>}</div>
              </div>
              <div className="col-span-2">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">Próxima acción</div>
                <div className={`text-sm ${lead.accion_vencida ? "text-red-300" : lead.accion_hoy ? "text-amber-300" : "text-zinc-200"}`}>
                  {lead.proxima_accion_fecha ? (
                    <>
                      {lead.accion_vencida && <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />}
                      {lead.proxima_accion_fecha} · {lead.proxima_accion || "—"}
                    </>
                  ) : <span className="italic text-zinc-600">— sin acción —</span>}
                </div>
              </div>
              {lead.notas && (
                <div className="col-span-2">
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500">Notas</div>
                  <div className="text-sm text-zinc-300 whitespace-pre-wrap">{lead.notas}</div>
                </div>
              )}
              {lead.estado === "perdido" && lead.motivo_perdida && (
                <div className="col-span-2 bg-red-500/10 border-l-4 border-red-500 p-2 rounded">
                  <div className="text-[10px] uppercase tracking-wider text-red-300">Motivo de pérdida</div>
                  <div className="text-sm text-zinc-200">{lead.motivo_perdida}</div>
                </div>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-3 text-sm">
              {[
                ["empresa", "Empresa"], ["persona_contacto", "Persona contacto"],
                ["email", "Email"], ["telefono", "Teléfono"],
                ["cif", "CIF"], ["web", "Web"],
              ].map(([k, label]) => (
                <div key={k} className={k === "empresa" ? "col-span-2" : ""}>
                  <Label className="text-xs text-zinc-400">{label}</Label>
                  <Input value={edit[k] || ""} onChange={(e) => setEdit({ ...edit, [k]: e.target.value })}
                    className="bg-zinc-900 border-zinc-800" data-testid={`edit-${k}`} />
                </div>
              ))}
              <div>
                <Label className="text-xs text-zinc-400">Fuente</Label>
                <Select value={edit.fuente} onValueChange={(v) => setEdit({ ...edit, fuente: v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    {meta.fuentes.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Comercial</Label>
                <Select value={edit.comercial_id || "__NONE__"}
                  onValueChange={(v) => setEdit({ ...edit, comercial_id: v === "__NONE__" ? "" : v })}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                    <SelectItem value="__NONE__">— sin asignar —</SelectItem>
                    {usuarios.filter((u) => (u.roles || []).some((r) => r === "comercial" || r === "admin")).map((u) => (
                      <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Importe (€)</Label>
                <Input type="number" step="0.01" value={edit.importe_estimado}
                  onChange={(e) => setEdit({ ...edit, importe_estimado: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Probabilidad (%)</Label>
                <Input type="number" min="0" max="100" value={edit.probabilidad}
                  onChange={(e) => setEdit({ ...edit, probabilidad: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-zinc-400">Descripción oportunidad</Label>
                <Textarea value={edit.descripcion_oportunidad}
                  onChange={(e) => setEdit({ ...edit, descripcion_oportunidad: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 min-h-[60px]" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-zinc-400 flex items-center gap-1">
                  <Building2 className="w-3 h-3" /> Cliente del directorio (opcional · si ya existe)
                </Label>
                <Select
                  value={edit.cliente_existente_id || "__NONE__"}
                  onValueChange={(v) => setEdit({ ...edit, cliente_existente_id: v === "__NONE__" ? "" : v })}
                >
                  <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="edit-cliente-existente">
                    <SelectValue placeholder="— ninguno (crearé un cliente nuevo al convertir) —" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-h-64">
                    <SelectItem value="__NONE__">— ninguno · crear nuevo —</SelectItem>
                    {clientesDir
                      .slice()
                      .sort((a, b) => (a.nombre || "").localeCompare(b.nombre || ""))
                      .map((c) => (
                        <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-zinc-500 mt-1">
                  Si lo eliges, este lead quedará vinculado al cliente existente (útil cuando el cliente ya tiene historial).
                </div>
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-zinc-400 flex items-center gap-1">
                  <Tag className="w-3 h-3" /> Etiquetas (#hashtags) — separa con coma o Enter
                </Label>
                <TagEditor
                  value={edit.etiquetas || []}
                  onChange={(tags) => setEdit({ ...edit, etiquetas: tags })}
                  sugerencias={["montaje", "produccion", "diseno", "rotulacion", "vinilo", "escaparate", "letras-corporeas", "urgente"]}
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400 flex items-center gap-1">
                  <MapPin className="w-3 h-3" /> Ubicación del trabajo
                </Label>
                <AddressAutocomplete
                  value={edit.ubicacion_espacio || ""}
                  onChange={(v) => setEdit({ ...edit, ubicacion_espacio: v })}
                  onSelect={(loc) => setEdit({ ...edit, ubicacion_espacio: loc.direccion })}
                  placeholder="Empieza a escribir: CC Holea, Mango Pg Gracia…"
                  data-testid="edit-ubicacion"
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400 flex items-center gap-1">
                  <MessageCircle className="w-3 h-3 text-emerald-400" /> URL grupo WhatsApp
                </Label>
                <Input
                  value={edit.whatsapp_group_url || ""}
                  onChange={(e) => setEdit({ ...edit, whatsapp_group_url: e.target.value })}
                  placeholder="https://chat.whatsapp.com/…"
                  data-testid="edit-wa-group"
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Próxima acción</Label>
                <Input value={edit.proxima_accion}
                  onChange={(e) => setEdit({ ...edit, proxima_accion: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" />
              </div>
              <div>
                <Label className="text-xs text-zinc-400">Fecha</Label>
                <Input type="date" value={edit.proxima_accion_fecha}
                  onChange={(e) => setEdit({ ...edit, proxima_accion_fecha: e.target.value })}
                  className="bg-zinc-900 border-zinc-800" />
              </div>
              <div className="col-span-2">
                <Label className="text-xs text-zinc-400">Notas internas</Label>
                <Textarea value={edit.notas}
                  onChange={(e) => setEdit({ ...edit, notas: e.target.value })}
                  className="bg-zinc-900 border-zinc-800 min-h-[60px]" />
              </div>
              <div className="col-span-2 flex justify-end gap-2">
                <Button variant="outline" onClick={() => setEditing(false)}
                  className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
                <Button onClick={saveEdit} className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
                  data-testid="lead-edit-save">Guardar cambios</Button>
              </div>
            </div>
          )}
        </div>

        {/* Presupuestos vinculados (columna de propiedades) */}
        <LeadPresupuestos lead={lead} onSaved={() => { load(); onChange?.(); }} />
        </aside>

        <div className="space-y-4 min-w-0 order-1" data-testid="lead-drawer-main">
        {/* Descripción rica + checklist (Fase 1 Asana-like) */}
        <LeadDescripcion lead={lead} onSaved={() => { load(); onChange?.(); }} readOnly={yaConvertido} />
        <LeadChecklist lead={lead} onSaved={load} readOnly={yaConvertido} />

        {/* Adjuntos / bolsa de ficheros */}
        <div
          className={`bg-zinc-900/40 border rounded-lg p-4 transition-colors ${
            draggingFile ? "border-amber-400 bg-amber-500/10" : "border-zinc-800"
          }`}
          onDragOver={(e) => { e.preventDefault(); setDraggingFile(true); }}
          onDragLeave={() => setDraggingFile(false)}
          onDrop={handleDrop}
          data-testid="lead-archivos"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-2">
              <Paperclip className="w-3.5 h-3.5" /> Archivos · bolsa del lead
              <span className="text-[10px] text-zinc-600 normal-case">
                ({(lead.archivos || []).length} archivos)
              </span>
            </h3>
            <div className="flex items-center gap-2">
              <LeadAddUrl leadId={leadId} onSaved={load} />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                hidden
                onChange={(e) => Array.from(e.target.files || []).forEach(uploadFile)}
                data-testid="lead-file-input"
              />
              <Button size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingFile}
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400 h-7 text-xs"
                data-testid="lead-upload-btn">
                {uploadingFile ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Upload className="w-3.5 h-3.5 mr-1" /> Subir</>}
              </Button>
            </div>
          </div>
          {(lead.archivos || []).length === 0 ? (
            <div className="text-center py-6 text-xs text-zinc-500 italic border-2 border-dashed border-zinc-800 rounded">
              {draggingFile ? "Suelta los archivos aquí…" : "Arrastra archivos aquí, o pulsa Subir · JPG, PDF, PPTX, Excel…"}
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {lead.archivos.map((a) => {
                const isImg = (a.tipo || "").startsWith("image/") || /\.(jpe?g|png|gif|webp)$/i.test(a.nombre || "");
                const isPres = a.origen === "presupuesto_aceptado";
                const isUrl = a.tipo === "url";
                return (
                  <div key={a.id} data-testid={`lead-file-${a.id}`}
                    className={`bg-zinc-950 border rounded-lg p-2 flex items-center gap-2 ${
                      isPres ? "border-emerald-500/40" : "border-zinc-800/80"
                    }`}>
                    {isImg ? (
                      <a href={a.url} target="_blank" rel="noreferrer" className="flex-shrink-0">
                        <img src={a.url} alt={a.nombre} className="w-16 h-16 object-cover rounded" loading="lazy" />
                      </a>
                    ) : isUrl ? (
                      <div className="w-12 h-12 flex items-center justify-center rounded flex-shrink-0 bg-sky-500/15 text-sky-200">
                        <Globe className="w-5 h-5" />
                      </div>
                    ) : (
                      <div className={`w-12 h-12 flex items-center justify-center rounded flex-shrink-0 ${
                        isPres ? "bg-emerald-500/20 text-emerald-200" : "bg-zinc-800 text-zinc-400"
                      }`}>
                        <FileText className="w-5 h-5" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <a href={a.url} target="_blank" rel="noreferrer"
                        className="text-sm text-zinc-100 hover:text-amber-300 truncate block" title={a.nombre}>
                        {a.nombre}
                      </a>
                      <div className="text-[10px] text-zinc-500 flex items-center gap-1 truncate">
                        {isPres && <Trophy className="w-3 h-3 text-emerald-400" />}
                        {isPres ? "Presupuesto aceptado" : isUrl ? (() => { try { return new URL(a.url).hostname; } catch { return "enlace"; } })() : (a.subido_por_nombre || "manual")}
                        {a.size_bytes ? ` · ${Math.round(a.size_bytes / 1024)} KB` : ""}
                      </div>
                    </div>
                    {!isPres && (
                      <button onClick={() => deleteFile(a.id, a.nombre)}
                        data-testid={`lead-file-del-${a.id}`}
                        className="text-zinc-600 hover:text-red-400 flex-shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Timeline */}
        <div className="bg-zinc-900/40 border border-zinc-800 rounded-lg p-4">
          <h3 className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-2 mb-3">
            <History className="w-3.5 h-3.5" /> Historial · @menciones notifican in-app + email
          </h3>
          <div className="relative mb-3">
            <div className="flex gap-2">
              <Input value={comentario}
                ref={comentarioRef}
                onChange={onComentarioChange}
                placeholder="Comentario… usa @ para mencionar (ej: @daniel mira esto)"
                onKeyDown={(e) => {
                  if (mentionPopup && mentionPopup.opts.length > 0) {
                    if (e.key === "Tab" || (e.key === "Enter" && e.shiftKey === false)) {
                      e.preventDefault();
                      pickMention(mentionPopup.opts[0]);
                      return;
                    }
                    if (e.key === "Escape") { setMentionPopup(null); return; }
                  }
                  if (e.key === "Enter" && !e.shiftKey) addComment();
                }}
                className="bg-zinc-900 border-zinc-800" data-testid="lead-comment-input" />
              <Button onClick={addComment} disabled={!comentario.trim()}
                className="bg-amber-500 text-zinc-950 hover:bg-amber-400" data-testid="lead-comment-send">
                <Send className="w-3.5 h-3.5" />
              </Button>
            </div>
            {mentionPopup && mentionPopup.opts.length > 0 && (
              <div data-testid="mention-popup"
                className="absolute z-50 left-0 bottom-full mb-1 w-72 bg-zinc-950 border border-amber-500/40 rounded shadow-lg overflow-hidden">
                <div className="text-[10px] px-2 py-1 bg-zinc-900 text-amber-300 border-b border-zinc-800 flex items-center gap-1">
                  <AtSign className="w-3 h-3" /> Tab/Enter para insertar
                </div>
                {mentionPopup.opts.map((u) => (
                  <button key={u.id} onClick={() => pickMention(u)}
                    data-testid={`mention-opt-${u.handle}`}
                    className="w-full flex items-center gap-2 px-2 py-1.5 hover:bg-amber-500/20 text-left">
                    {u.foto_url ? (
                      <img src={u.foto_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-amber-500/30 text-amber-200 text-[10px] flex items-center justify-center font-bold">
                        {(u.name || "?").slice(0, 1).toUpperCase()}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-zinc-100 truncate">{u.name}</div>
                      <div className="text-[10px] text-zinc-500">@{u.handle}</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="lead-timeline">
            {(lead.timeline || []).slice().reverse().map((t) => (
              <div key={t.id} className={`bg-zinc-950 border rounded p-2.5 text-sm ${
                t.tipo === "archivo" ? "border-amber-500/30" :
                t.tipo === "sistema" ? "border-emerald-500/30" :
                "border-zinc-800/60"
              }`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    {t.tipo === "comentario" && <MessageSquare className="w-3 h-3 text-zinc-500" />}
                    {t.tipo === "estado" && <ArrowRight className="w-3 h-3 text-blue-400" />}
                    {t.tipo === "conversion" && <Trophy className="w-3 h-3 text-emerald-400" />}
                    {t.tipo === "archivo" && <Paperclip className="w-3 h-3 text-amber-400" />}
                    {t.tipo === "sistema" && <Trophy className="w-3 h-3 text-emerald-400" />}
                    <span className="text-[11px] font-medium text-zinc-300">{t.autor_nombre || "sistema"}</span>
                    {(t.menciones_ids || []).length > 0 && (
                      <span className="text-[10px] text-amber-300 flex items-center gap-1">
                        <AtSign className="w-2.5 h-2.5" /> {t.menciones_ids.length}
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] text-zinc-500">{(t.created_at || "").slice(0, 16).replace("T", " ")}</span>
                </div>
                <div className="text-zinc-200 whitespace-pre-wrap" data-testid={`timeline-${t.id}-text`}>
                  <RenderConMenciones text={t.descripcion} mencionables={mencionables} />
                </div>
              </div>
            ))}
            {!(lead.timeline || []).length && <div className="text-center text-xs text-zinc-600 italic py-4">— sin movimientos —</div>}
          </div>
        </div>
        </div>{/* /main */}
        </div>{/* /grid */}
        </div>{/* /padding */}

        {/* Diálogo convertir */}
        <Dialog open={openConvert} onOpenChange={setOpenConvert}>
          <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="lead-convert-dialog">
            <DialogHeader>
              <DialogTitle>🏆 Convertir a Cliente</DialogTitle>
              <DialogDescription className="text-zinc-500 text-xs">
                {lead.cliente_existente_id ? "Se vinculará al cliente existente del directorio (no se duplica)." : "Se creará un Cliente nuevo con los datos del lead (si el CIF ya existe, se reutiliza)."}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={convForm.crear_peticion}
                  onChange={(e) => setConvForm({ ...convForm, crear_peticion: e.target.checked })}
                  className="accent-amber-500" data-testid="convert-crear-peticion" />
                También crear primera petición (borrador)
              </label>
              {convForm.crear_peticion && (
                <>
                  <div>
                    <Label className="text-xs text-zinc-400">Nombre de la petición</Label>
                    <Input value={convForm.peticion_nombre}
                      onChange={(e) => setConvForm({ ...convForm, peticion_nombre: e.target.value })}
                      className="bg-zinc-900 border-zinc-800" data-testid="convert-pet-nombre" />
                  </div>
                  <div>
                    <Label className="text-xs text-zinc-400">Descripción</Label>
                    <Textarea value={convForm.peticion_descripcion}
                      onChange={(e) => setConvForm({ ...convForm, peticion_descripcion: e.target.value })}
                      placeholder={lead.descripcion_oportunidad || ""}
                      className="bg-zinc-900 border-zinc-800 min-h-[80px]" />
                  </div>
                </>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenConvert(false)}
                className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
              <Button onClick={convertir} className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400"
                data-testid="convert-confirm">
                <Trophy className="w-4 h-4 mr-2" /> Convertir
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Diálogo perdido */}
        <Dialog open={openPerdido} onOpenChange={setOpenPerdido}>
          <DialogContent className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-md" data-testid="lead-perdido-dialog">
            <DialogHeader>
              <DialogTitle>Marcar como perdido</DialogTitle>
              <DialogDescription className="text-zinc-500 text-xs">
                Indica el motivo para análisis posterior.
              </DialogDescription>
            </DialogHeader>
            <div>
              <Label className="text-xs text-zinc-400">Motivo de la pérdida *</Label>
              <Textarea value={motivoPerdida}
                onChange={(e) => setMotivoPerdida(e.target.value)}
                placeholder="Precio, plazos, competencia, sin presupuesto…"
                className="bg-zinc-900 border-zinc-800 min-h-[80px]"
                data-testid="perdido-motivo" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpenPerdido(false)}
                className="bg-transparent border-zinc-700 text-zinc-300">Cancelar</Button>
              <Button onClick={confirmPerdido} className="bg-red-500 text-zinc-950 hover:bg-red-400"
                data-testid="perdido-confirm">Marcar perdido</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, value, icon: Icon, link, external }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm text-zinc-200 truncate">
        {value ? (
          link ? (
            <a href={link} target={external ? "_blank" : undefined} rel="noreferrer"
              className="text-amber-300 hover:underline flex items-center gap-1">
              {Icon && <Icon className="w-3 h-3" />}
              {value}
              {external && <ExternalLink className="w-3 h-3" />}
            </a>
          ) : (
            <span className="flex items-center gap-1">
              {Icon && <Icon className="w-3 h-3 text-zinc-500" />}
              {value}
            </span>
          )
        ) : <span className="italic text-zinc-600">—</span>}
      </div>
    </div>
  );
}

// ============ TAG EDITOR ============
// Chips de etiquetas tipo Monday: añadir por coma o Enter, borrar con X, sugerencias rápidas.
function TagEditor({ value = [], onChange, sugerencias = [] }) {
  const [input, setInput] = useState("");

  const slug = (s) =>
    s
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9-]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

  const addTag = (raw) => {
    const t = slug(raw);
    if (!t) return;
    if (value.includes(t)) return;
    onChange([...value, t]);
    setInput("");
  };
  const removeTag = (t) => onChange(value.filter((x) => x !== t));

  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded p-2 space-y-2">
      <div className="flex flex-wrap gap-1.5 min-h-[24px]">
        {value.map((t) => (
          <span key={t} data-testid={`tag-${t}`}
            className="text-[11px] px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-100 border border-violet-500/40 flex items-center gap-1">
            #{t}
            <button onClick={() => removeTag(t)} className="text-violet-300 hover:text-red-400" type="button">
              <X className="w-2.5 h-2.5" />
            </button>
          </span>
        ))}
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              if (input.trim()) addTag(input.trim());
            } else if (e.key === "Backspace" && !input && value.length > 0) {
              removeTag(value[value.length - 1]);
            }
          }}
          placeholder={value.length === 0 ? "Escribe y pulsa Enter (ej: montaje, urgente…)" : ""}
          data-testid="tag-editor-input"
          className="bg-transparent text-sm text-zinc-100 outline-none flex-1 min-w-[140px]"
        />
      </div>
      {sugerencias.filter((s) => !value.includes(s)).length > 0 && (
        <div className="flex flex-wrap gap-1 pt-1 border-t border-zinc-800">
          <span className="text-[10px] text-zinc-500 self-center mr-1">Sugerencias:</span>
          {sugerencias.filter((s) => !value.includes(s)).map((s) => (
            <button key={s} onClick={() => addTag(s)} type="button"
              data-testid={`tag-sug-${s}`}
              className="text-[10px] px-1.5 py-0.5 rounded bg-zinc-800 text-zinc-400 hover:bg-violet-500/20 hover:text-violet-200 transition-colors">
              +#{s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ============ RENDER CON MENCIONES ============
// Resalta @handles en el texto del timeline. Si coincide con un usuario, lo pinta en ámbar.
function RenderConMenciones({ text, mencionables = [] }) {
  if (!text) return null;
  const parts = String(text).split(/(@[A-Za-zÁÉÍÓÚÜÑáéíóúüñ0-9_.]+)/g);
  return (
    <>
      {parts.map((p, i) => {
        if (p.startsWith("@")) {
          const handle = p.slice(1).toLowerCase()
            .normalize("NFD").replace(/[\u0300-\u036f]/g, "");
          const u = mencionables.find((m) => m.handle === handle);
          if (u) {
            return (
              <span key={i} className="bg-amber-500/20 text-amber-200 px-1 rounded font-medium" title={u.name}>
                @{u.handle}
              </span>
            );
          }
        }
        return <span key={i}>{p}</span>;
      })}
    </>
  );
}
